/**
 * Color palette for OEM badges across admin Setup pages.
 *
 * UX intent: each OEM should be visually distinct enough that admins
 * scanning a list of tenants can recognize "Ford rows" or "Tesla rows"
 * without reading the label. Colors stay subtle (tinted backgrounds,
 * not loud brand colors) so the table remains scannable. Each OEM gets
 * a stable assignment so the same OEM always renders with the same
 * color across pages and reloads.
 *
 * Design notes:
 * - Light mode uses 100-tier backgrounds with 700-tier text (high contrast).
 * - Dark mode uses 950/30 backgrounds with 300-tier text (low brightness,
 *   readable).
 * - Border tints in 200/800 keep the badge outline visible without dominating.
 * - "Other" is intentionally muted — it's a catch-all, not a brand.
 */

export interface OemTheme {
  /** Tailwind classes for the badge container (bg + text + border). */
  className: string;
  /** Optional dot accent color for compact rendering (e.g., legends). */
  dotClassName: string;
}

/**
 * Hex-color counterpart to {@link OemTheme}, used by canvas/SVG renderers
 * (Leaflet polygons, html2canvas exports) where Tailwind class names can't
 * be resolved at runtime. The fill matches each OEM's `bg-*-500` accent and
 * the stroke matches the `*-700` tier — readable as an outline against the
 * fill without dominating it.
 */
export interface OemHexColor {
  fill: string;
  stroke: string;
}

const OEM_THEMES: Record<string, OemTheme> = {
  Ford: {
    className:
      'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900',
    dotClassName: 'bg-blue-500'
  },
  Stellantis: {
    className:
      'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900',
    dotClassName: 'bg-rose-500'
  },
  'General Motors': {
    className:
      'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900',
    dotClassName: 'bg-amber-500'
  },
  Toyota: {
    className:
      'bg-red-100 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900',
    dotClassName: 'bg-red-500'
  },
  Honda: {
    className:
      'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-900',
    dotClassName: 'bg-orange-500'
  },
  Nissan: {
    className:
      'bg-slate-200 text-slate-800 border-slate-300 dark:bg-slate-800/60 dark:text-slate-200 dark:border-slate-700',
    dotClassName: 'bg-slate-500'
  },
  Hyundai: {
    className:
      'bg-cyan-100 text-cyan-800 border-cyan-200 dark:bg-cyan-950/40 dark:text-cyan-300 dark:border-cyan-900',
    dotClassName: 'bg-cyan-500'
  },
  Kia: {
    className:
      'bg-pink-100 text-pink-800 border-pink-200 dark:bg-pink-950/40 dark:text-pink-300 dark:border-pink-900',
    dotClassName: 'bg-pink-500'
  },
  Volkswagen: {
    className:
      'bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-900',
    dotClassName: 'bg-indigo-500'
  },
  BMW: {
    className:
      'bg-sky-100 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-900',
    dotClassName: 'bg-sky-500'
  },
  'Mercedes-Benz': {
    className:
      'bg-zinc-200 text-zinc-800 border-zinc-300 dark:bg-zinc-800/60 dark:text-zinc-200 dark:border-zinc-700',
    dotClassName: 'bg-zinc-500'
  },
  Audi: {
    className:
      'bg-stone-200 text-stone-800 border-stone-300 dark:bg-stone-800/60 dark:text-stone-200 dark:border-stone-700',
    dotClassName: 'bg-stone-500'
  },
  Volvo: {
    className:
      'bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-950/40 dark:text-teal-300 dark:border-teal-900',
    dotClassName: 'bg-teal-500'
  },
  Subaru: {
    className:
      'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900',
    dotClassName: 'bg-emerald-500'
  },
  Mazda: {
    className:
      'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200 dark:bg-fuchsia-950/40 dark:text-fuchsia-300 dark:border-fuchsia-900',
    dotClassName: 'bg-fuchsia-500'
  },
  Tesla: {
    className:
      'bg-neutral-200 text-neutral-900 border-neutral-300 dark:bg-neutral-900 dark:text-neutral-100 dark:border-neutral-800',
    dotClassName: 'bg-neutral-700'
  },
  Rivian: {
    className:
      'bg-lime-100 text-lime-800 border-lime-200 dark:bg-lime-950/40 dark:text-lime-300 dark:border-lime-900',
    dotClassName: 'bg-lime-500'
  },
  Lucid: {
    className:
      'bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-900',
    dotClassName: 'bg-violet-500'
  },
  Other: {
    className:
      'bg-muted text-muted-foreground border-border',
    dotClassName: 'bg-muted-foreground'
  }
};

const FALLBACK_THEME: OemTheme = OEM_THEMES['Other']!;

/**
 * Returns the badge theme for a given OEM name. Unknown OEMs fall back to
 * the muted "Other" theme so the table never breaks if a new OEM appears
 * before the palette is updated.
 */
export function getOemTheme(oem: string | null | undefined): OemTheme {
  if (!oem) return FALLBACK_THEME;
  return OEM_THEMES[oem] ?? FALLBACK_THEME;
}

/**
 * Hex-color map for OEM brand fills/strokes in canvas-rendered surfaces.
 * Keys must match {@link OEM_THEMES} so any OEM with a Tailwind theme also
 * has a hex theme. Tailwind v3 palette values; fill = `*-500`, stroke = `*-700`.
 */
const OEM_HEX_COLORS: Record<string, OemHexColor> = {
  Ford:             { fill: '#3b82f6', stroke: '#1d4ed8' }, // blue
  Stellantis:       { fill: '#f43f5e', stroke: '#be123c' }, // rose
  'General Motors': { fill: '#f59e0b', stroke: '#b45309' }, // amber
  Toyota:           { fill: '#ef4444', stroke: '#b91c1c' }, // red
  Honda:            { fill: '#f97316', stroke: '#c2410c' }, // orange
  Nissan:           { fill: '#64748b', stroke: '#334155' }, // slate
  Hyundai:          { fill: '#06b6d4', stroke: '#0e7490' }, // cyan
  Kia:              { fill: '#ec4899', stroke: '#be185d' }, // pink
  Volkswagen:       { fill: '#6366f1', stroke: '#4338ca' }, // indigo
  BMW:              { fill: '#0ea5e9', stroke: '#0369a1' }, // sky
  'Mercedes-Benz':  { fill: '#71717a', stroke: '#3f3f46' }, // zinc
  Audi:             { fill: '#78716c', stroke: '#44403c' }, // stone
  Volvo:            { fill: '#14b8a6', stroke: '#0f766e' }, // teal
  Subaru:           { fill: '#10b981', stroke: '#047857' }, // emerald
  Mazda:            { fill: '#d946ef', stroke: '#a21caf' }, // fuchsia
  Tesla:            { fill: '#404040', stroke: '#171717' }, // neutral
  Rivian:           { fill: '#84cc16', stroke: '#4d7c0f' }, // lime
  Lucid:            { fill: '#8b5cf6', stroke: '#6d28d9' }, // violet
  Other:            { fill: '#94a3b8', stroke: '#475569' }  // slate-400/600 — muted
};

const FALLBACK_HEX: OemHexColor = OEM_HEX_COLORS['Other']!;

/**
 * Returns the hex `{ fill, stroke }` colors for a given OEM, used by Leaflet
 * polygons and html2canvas exports where Tailwind classes can't be resolved.
 * Mirrors {@link getOemTheme}'s fallback behavior.
 */
export function getOemHexColor(oem: string | null | undefined): OemHexColor {
  if (!oem) return FALLBACK_HEX;
  return OEM_HEX_COLORS[oem] ?? FALLBACK_HEX;
}

/**
 * Four-shade tonal palette of Vehix orange used by Presentation-mode zone
 * fills. Adjacent zones get assigned different palette indexes via a
 * four-color graph-coloring pass (see {@link
 * ../utils/zoneAdjacencyColoring} `computeZoneColorAssignment`) so two
 * touching zones never share a fill — the boundary becomes a contrast
 * edge between two color fields rather than a stroke trying to separate
 * same-color regions.
 *
 * Shades are spaced ~20% apart in OKLCH lightness (the perceptually-
 * uniform color space) so the eye reads them as evenly distinct, not
 * just hex-evenly-spaced. All four are warm-brown family, so the map
 * still reads as a Vehix-branded artifact rather than a multi-color
 * rainbow.
 */
export const VEHIX_ZONE_PALETTE: ReadonlyArray<{ fill: string; stroke: string }> = [
  { fill: '#e8a574', stroke: '#a8552c' }, // light tint
  { fill: '#d4854a', stroke: '#a8552c' }, // brand orange
  { fill: '#a8552c', stroke: '#6b3a1f' }, // burnt sienna
  { fill: '#6b3a1f', stroke: '#3d2110' }  // dark brown
];

/**
 * Presentation-mode color for zones already assigned to a tenant. Neutral
 * slate, deliberately outside the warm palette above: assigned zones recede
 * so the zones still available for sale are what the eye lands on. Always
 * paired with a dashed stroke so the distinction never rests on color alone.
 */
export const ASSIGNED_ZONE_COLOR = { fill: '#94a3b8', stroke: '#64748b' } as const;

/**
 * The brand-orange shade of VEHIX_ZONE_PALETTE, used wherever "available" needs
 * one representative swatch (legends, availability dots, label text).
 */
export const AVAILABLE_ZONE_COLOR = { fill: '#d4854a', stroke: '#a8552c' } as const;

/**
 * Availability badge on Presentation-mode zone labels and the PNG legend. Solid
 * colors (no alpha) because html2canvas renders translucent backgrounds unevenly.
 * Both pairs clear WCAG AA for small text: available 5.3:1, assigned 6.5:1.
 */
export const ZONE_AVAILABILITY_BADGE = {
  available: { background: '#fbeadc', color: '#9a4a22' },
  assigned: { background: '#e9eef4', color: '#475569' }
} as const;
